import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import store from "redux-share/store";
import { handleCloseCommentPopover, handleCommentsIdsInLocalStorage, handleGetComments } from "redux-share/actions/comment.action";
import { IComments, ConditionName, IUsersCommentsProfiles } from "interfaces";
import { conditions } from "helpers";
import { setCommentPopover } from "redux-share/slices/popoversSlice";
import useIntersect from "helpers/hooks/useIntersect";
import { shallowEqual } from "react-redux";
import dynamic from "next/dynamic";

const ChevronLeftIcon = dynamic(() => import("../../icon-components/chevronLeft"));
const CommentsPopover = dynamic(() => import("./commentsPopover"));
const SingleComment = dynamic(() => import("./singleComment"));
const AddComment = dynamic(() => import("./addComment"));
const Skeleton = dynamic(() => import("../skeleton/skeleton"));

const Comments = () => {
    const dispatch = useAppDispatch();
    const { commentPopover } = useAppSelector(({ popovers: { commentPopover } }) => ({ commentPopover }), shallowEqual);
    const { comments, commentsCount, usersCommentsProfiles } = useAppSelector(
        ({ comments: { comments, commentsCount, usersCommentsProfiles } }) => ({ comments, commentsCount, usersCommentsProfiles }),
        shallowEqual
    );
    const { loadedFragmentsCount } = useAppSelector(({ controller: { loadedFragmentsCount } }) => ({ loadedFragmentsCount }), shallowEqual);
    const { userId, name, avatar_url } = useAppSelector(({ user: { userId, name, avatar_url } }) => ({ userId, name, avatar_url }), shallowEqual);

    const [scrollEnd, setScrollEnd] = useState<boolean>(false);
    const handleIntersect = ([entry]: IntersectionObserverEntry[]) => {
        const { comments, commentsCountPagination } = store.getState().comments;
        if (entry.isIntersecting && (comments === undefined || comments.length !== commentsCountPagination)) setScrollEnd(true);
    };

    useEffect(() => {
        if (!conditions(ConditionName.isTouchScreen) && conditions(ConditionName.hasPermittedLoadedFragment)) handleGetComments(0, setScrollEnd);
    }, [loadedFragmentsCount.length]);

    useEffect(() => {
        if (scrollEnd && (comments === undefined || comments.length)) handleGetComments(comments === undefined ? 0 : comments.length, setScrollEnd);
    }, [scrollEnd]);

    const handleShowCommentsPopover = () => {
        handleGetComments(0, setScrollEnd);
        dispatch(setCommentPopover(true));
    };

    const observerRef = useIntersect(handleIntersect);

    const handleCommentUserProfile = (comment: IComments) => {
        if (userId === comment.author) {
            return {
                displayName: name,
                id: userId,
                image: avatar_url
            };
        } else {
            return usersCommentsProfiles.find((profile: IUsersCommentsProfiles) => profile.id === comment.author);
        }
    };

    const commentsContent = conditions(ConditionName.isTouchScreen) ? (
        <>
            <div
                className="pl-flex pl-justify-between pl-items-center pl-mx-3 pl-bg-gray-highLight dark:pl-bg-dark-3 pl-p-4 pl-rounded-[8px] pl-my-4"
                onClick={handleShowCommentsPopover}
            >
                <p className="pl-text-[15px] pl-font-normal">{!!commentsCount ? `نمایش ${commentsCount} دیدگاه` : "دیدگاه خود را وارد کنید"}</p>
                <ChevronLeftIcon className="pl-w-4 pl-h-4 pl-stroke-gray-800/[0.7] dark:pl-stroke-secondary-300 -pl-rotate-90" />
            </div>
            <CommentsPopover status={commentPopover} onClose={handleCloseCommentPopover} setScrollEnd={setScrollEnd} />
        </>
    ) : (
        <>
            <AddComment type="comment" />
            {comments !== undefined && comments.length > 0 && (
                <div className="pl-flex pl-flex-col pl-gap-2 pl-mb-[80px] pl-mt-6">
                    {comments.map((comment: IComments) => (
                        <SingleComment
                            key={comment.id}
                            comment={comment}
                            user={handleCommentUserProfile(comment)}
                            commentsLocalStorageIds={handleCommentsIdsInLocalStorage()}
                        />
                    ))}
                </div>
            )}
            <div id="observer" className="pl-bg-transparent pl-pt-5" ref={observerRef} />
        </>
    );

    return (
        <>
            {conditions(ConditionName.isBeforeRequestComments) ? (
                <>
                    <div className={window.innerWidth < 900 ? "pl-mt-3 pl-w-full" : ""}>
                        {Array.from(Array(2).keys()).map((item, index) => (
                            <Skeleton key={index} />
                        ))}
                    </div>
                </>
            ) : (
                commentsContent
            )}
        </>
    );
};

export default Comments;
